import React from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Button, Card, DatePicker } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { fetchAttendance } from "../attendanceEntryThunk";
import { getMonth, getYear } from "../../../lib/yearMonth";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import { setFilters } from "../attendanceEntrySlice";
import { dateTimeFormat } from "../../../lib/functions";

const AttendanceFilters = () => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.attendance);

  const months = getMonth();
  const years = getYear();

  const safeDate =
    filters.attendanceDate &&
    dayjs(filters.attendanceDate, "YYYY-MM-DD", true).isValid()
      ? dayjs(filters.attendanceDate, "YYYY-MM-DD")
      : dayjs();

  const handleSearch = () => {
    const payload = {
      employeeId: filters?.employee_id || null,
      year: filters?.year,
      month: filters?.month,
      attendanceDate: filters.attendanceDate,
      selectedFilterType: "All",
      typeId: filters.typeId || null,
    };
    dispatch(fetchAttendance(payload));
  };

  const handleAttendanceDateChange = (date) => {
    if (!date) return;

    dispatch(
      setFilters({
        attendanceDate: date.format("YYYY-MM-DD"),
        year: date.format("YYYY"),
        month: date.format("MMMM"),
      })
    );
  };

  const handleYearChange = (year) => {
    if (!year) return;

    const baseDate =
      filters.attendanceDate &&
      dayjs(filters.attendanceDate, "YYYY-MM-DD", true).isValid()
        ? dayjs(filters.attendanceDate, "YYYY-MM-DD")
        : dayjs();

    const updatedDate = baseDate.year(Number(year));

    dispatch(
      setFilters({
        year: String(year),
        month: updatedDate.format("MMMM"),
        attendanceDate: updatedDate.format("YYYY-MM-DD"),
      })
    );
  };

  const handleMonthChange = (month) => {
    if (!month) return;

    const monthIndex = months.findIndex((m) => m.month === month);
    if (monthIndex === -1) return;

    const baseDate =
      filters.attendanceDate &&
      dayjs(filters.attendanceDate, "YYYY-MM-DD", true).isValid()
        ? dayjs(filters.attendanceDate, "YYYY-MM-DD")
        : dayjs();

    const updatedDate = baseDate.month(monthIndex);

    dispatch(
      setFilters({
        month,
        year: updatedDate.format("YYYY"),
        attendanceDate: updatedDate.format("YYYY-MM-DD"),
      })
    );
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} align="middle" className="attendance-filters">
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <label>Attendance Year</label>
          <SearchListAutoComplete
            optionsList={years.map((m) => ({
              sale_id: String(m.year),
              display_text: String(m.year),
            }))}
            value={{
              value: String(filters.year || safeDate.format("YYYY")),
              label: String(filters.year || safeDate.format("YYYY")),
            }}
            onSelect={(obj) => handleYearChange(obj?.sale_id)}
            placeholder="Year"
            showAddNew={false}
            data-testid="select-attendance-year"
          />
        </Col>

        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <label>Attendance Month</label>
          <SearchListAutoComplete
            optionsList={months.map((m) => ({
              sale_id: m.month,
              display_text: m.month,
            }))}
            value={{
              value: filters.month || safeDate.format("MMMM"),
              label: filters.month || safeDate.format("MMMM"),
            }}
            onSelect={(obj) => handleMonthChange(obj?.sale_id)}
            placeholder="Month"
            showAddNew={false}
            data-testid="select-attendance-month"
          />
        </Col>

        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ height: "64px" }}>
          <label>Attendance Date</label>
          <DatePicker
            value={safeDate}
            format={dateTimeFormat}
            onChange={handleAttendanceDateChange}
            style={{ width: "100%" }}
            allowClear={false}
            data-testid="attendance-date-picker"
          />
        </Col>

        <Col
          xs={24}
          sm={12}
          md={8}
          lg={6}
          xl={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            block
            data-testid="attendance-check-button"
          >
            Check
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default AttendanceFilters;